'use client'

import { formatPrice } from '@polaris/core/utils'
import { useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import {
  type Avatar,
  AvatarImage,
  DEFAULT_AVATAR,
} from '@/src/components/accounts/avatar'
import { RadialProgress } from '@/src/components/radial-progress'
import { useHasAccounts } from '@/src/hooks/account/use-has-accounts'
import { useUserSettings } from '@/src/hooks/use-user-settings'
import { preferredAvatarAtom } from '@/src/stores/accounts/atoms'
import { accountsFilteredBalancesAtom } from '@/src/stores/balances/accounts'
import { usePortfolioDrawer } from '@/src/stores/portfolio-drawer/actions'
import { routeTimingAtom } from '@/src/stores/route-machine/atoms'
import { cn } from '@/src/utils/styles'
import type { User } from '../../services/user'
import { Button } from '../ui/button'
import ConnectWalletButton from '../ui/connect-wallet-button'
import { Skeleton } from '../ui/skeleton'
import { Spinner } from '../ui/spinner'

type LoadingState = 'idle' | 'loading' | 'loaded'

export function PortfolioButton({ user }: { user: User }) {
  const { totalBalance } = useAtomValue(accountsFilteredBalancesAtom)
  const { isOpen, open } = usePortfolioDrawer()
  const { userSettings } = useUserSettings()
  const hasAccounts = useHasAccounts()

  const [loadingState, setLoadingState] = useState<LoadingState>(
    hasAccounts ? 'idle' : 'loaded',
  )
  const [debouncedBalance] = useDebounce(totalBalance, 500)

  const isLoading = loadingState === 'loading'

  useEffect(() => {
    // Start loading if balance changes and we haven't loaded yet
    if (totalBalance !== debouncedBalance && loadingState === 'idle') {
      setLoadingState('loading')
    }

    // Mark as loaded when debounced balance catches up
    if (
      (totalBalance &&
        debouncedBalance === totalBalance &&
        loadingState === 'loading') ||
      !hasAccounts
    ) {
      setLoadingState('loaded')
    }
  }, [totalBalance, debouncedBalance, loadingState, hasAccounts])

  // With polaris accounts, a user does not need a wallet to be connected to view portfolio drawer
  return hasAccounts || user.id !== 'guest' ? (
    <div
      className={cn(
        'flex items-center gap-3 transition-all duration-500 ease-default xl:max-w-44',
        {
          'opacity-0 pointer-events-none xl:max-w-0 xl:translate-x-full xl:-ml-2':
            isOpen,
        },
      )}
    >
      {loadingState === 'loading' ? (
        <div className='min-h-8 sm:min-h-12 sm:p-2 sm:pr-4 flex justify-center items-center rounded-3xl gap-3 sm:border border-border1'>
          <Skeleton className='h-8 w-8 rounded-full bg-tertiary animate-none' />
          <Skeleton className='h-3 w-11 rounded-lg hidden sm:block' />
        </div>
      ) : (
        <Button
          type='button'
          size={{
            default: 'sm',
            sm: 'md',
          }}
          variant='outline'
          data-testid='total-balance-btn'
          className='px-0 sm:pl-2 sm:pr-3 bg-background-80 backdrop-blur-lg gap-3 border-0 sm:border-1'
          onClick={open}
        >
          <Icon user={user} />
          <span
            className={cn(
              'hidden sm:inline text-sm md:text-base text-primary font-base leading-6 font-normal',
              {
                'animate-pulse-opacity': isLoading,
                'font-extralight !text-xl h-4 sm:h-5 text-tertiary-100':
                  userSettings.maskBalances,
              },
            )}
          >
            {!userSettings.maskBalances ? formatPrice(totalBalance) : '*****'}
          </span>
        </Button>
      )}
    </div>
  ) : (
    <ConnectWalletButton
      from='navbar'
      className={isOpen ? 'opacity-0 pointer-events-none' : undefined}
    />
  )
}

const Icon = ({ user }: { user: User }) => {
  const avatar = useAtomValue(preferredAvatarAtom)
  const routeTiming = useAtomValue(routeTimingAtom)
  const hasCurrentTrade = Boolean(routeTiming)
  const startMsTop = routeTiming?.startMs
  const endMsTop = routeTiming?.endMs

  let icon: Avatar | 'guest' | undefined
  if (user.id === 'guest') {
    icon = 'guest'
  } else if (avatar !== 'not-set') {
    // This should not happen as the user should have
    // onboarded and chosen either guest or a default or custom avatar
    icon = avatar
  } else {
    icon = DEFAULT_AVATAR
  }

  return (
    <div className='size-8 relative flex items-center justify-center'>
      <div
        className={cn(
          'size-8 absolute inset-0 opacity-0 transition-opacity duration-300',
          { 'opacity-100': hasCurrentTrade },
        )}
      >
        {hasCurrentTrade ? (
          startMsTop && endMsTop ? (
            <RadialProgress
              animated
              startTime={startMsTop}
              endTime={endMsTop}
              strokeWidth={1}
              progressClasses='-scale-y-100'
              circleClasses='text-accent-primary'
            />
          ) : (
            <Spinner className='size-6' />
          )
        ) : null}
      </div>
      <div
        className={cn(
          'size-8 rounded-full overflow-hidden transition-transform duration-300',
          hasCurrentTrade ? 'scale-[.66]' : '',
        )}
      >
        {icon === 'guest' ? (
          <AvatarImage avatar='cloud-man' className='size-8 object-cover' />
        ) : icon ? (
          <AvatarImage avatar={icon} className='size-8 object-cover' />
        ) : (
          <Skeleton className='size-8 rounded-full' />
        )}
      </div>
    </div>
  )
}
